import { useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  BarChart2,
  Bell,
  Briefcase,
  CalendarCheck2,
  CalendarDays,
  ClipboardCheck,
  FilePlus2,
  Settings,
  Users,
} from "lucide-react";

import { useAuth } from "#/context/AuthProvider";
import { useFeatureHint } from "#/features/feature-hints/hooks";
import { LeaveError } from "../components/LeaveError";
import { LeaveLoading } from "../components/LeaveLoading";
import { useAsyncData } from "../hooks";
import { leaveApi } from "../api";

// ─── helpers ──────────────────────────────────────────────────────────────────

function fmtDate(d: string) {
  try {
    return new Date(`${d}T00:00:00`).toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return d;
  }
}

function daysUntil(dateStr: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${dateStr}T00:00:00`);
  return Math.ceil((target.getTime() - today.getTime()) / 86_400_000);
}

// ─── page ─────────────────────────────────────────────────────────────────────

export default function LeaveDashboardPage() {
  const { hasRole, hasAnyRole, hasPermission } = useAuth();
  const [peopleTab, setPeopleTab] = useState<"leave" | "duty">("leave");

  const isAdmin        = hasRole("SUPER_ADMIN") || hasAnyRole(["ADMIN"]);
  const canApply       = isAdmin || hasPermission("HR_LEAVE_APPLY")   || hasPermission("HR_LEAVE_MANAGE");
  const canApprove     = isAdmin || hasPermission("HR_LEAVE_APPROVE") || hasPermission("HR_LEAVE_MANAGE");
  const canManage      = isAdmin || hasPermission("HR_LEAVE_MANAGE");
  const canViewAll     = isAdmin || hasPermission("HR_LEAVE_VIEW") || hasPermission("HR_LEAVE_APPROVE") || hasPermission("HR_LEAVE_MANAGE");
  const canViewReports = isAdmin || hasPermission("HR_LEAVE_REPORT_VIEW") || hasPermission("HR_LEAVE_MANAGE");

  const setup = useAsyncData(
    () => (canManage ? leaveApi.setupStatus() : Promise.resolve(null)),
    [canManage],
  );
  const onLeave = useAsyncData(() => leaveApi.whoIsOnLeave(), []);
  const dashboardSummary = useAsyncData(
    () => (canApply ? leaveApi.myDashboardSummary() : Promise.resolve(null)),
    [canApply],
  );
  const today = new Date().toISOString().slice(0, 10);
  const myPlans = useAsyncData(
    () => (canApply ? leaveApi.myLeavePlans() : Promise.resolve(null)),
    [canApply],
  );
  const in90 = new Date(Date.now() + 90 * 86_400_000).toISOString().slice(0, 10);
  const holidays = useAsyncData(() => leaveApi.holidaysBetween(today, in90), []);
  const nextHoliday = holidays.data?.[0] ?? null;

  const upcomingPlans = (myPlans.data ?? [])
    .filter((p) => p.startDate >= today && p.status !== "CANCELLED")
    .sort((a, b) => a.startDate.localeCompare(b.startDate));

  const reminderPlans = upcomingPlans.filter((p) => {
    if (!p.reminderEnabled) return false;
    const days = daysUntil(p.startDate);
    const threshold = p.reminderDaysBefore ?? 14;
    return days >= 0 && days <= threshold;
  });
  const reminderHint = useFeatureHint("LEAVE_REMINDER_BANNER", 5);

  const onLeaveList = onLeave.data?.currentlyOnLeave ?? [];
  const onDutyList  = onLeave.data?.onOfficialDuty ?? [];

  return (
    <div className="flex flex-col gap-4">

      {/* ── Header: title + holiday pill + apply CTA ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-black text-slate-900">Leave Management</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {nextHoliday && (
            <div className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-800">
              <CalendarDays className="h-3.5 w-3.5 text-emerald-600" />
              <span>{nextHoliday.name}</span>
              <span className="rounded-full bg-emerald-200 px-1.5 py-0.5 text-xs font-black text-emerald-800">
                {(() => {
                  const d = daysUntil(nextHoliday.holidayDate);
                  return d === 0 ? "Today!" : d === 1 ? "Tomorrow" : `in ${d}d`;
                })()}
              </span>
            </div>
          )}
          {canApply && (
            <Link
              to="/hr/leave/requests/new"
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-4 py-2 text-sm font-bold text-white transition-colors hover:bg-emerald-800"
            >
              <FilePlus2 className="h-4 w-4" />
              Apply for Leave
            </Link>
          )}
        </div>
      </div>

      {/* ── Setup incomplete warning (slim bar) ── */}
      {canManage && setup.data && !setup.data.complete && (
        <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5">
          <AlertCircle className="h-4 w-4 shrink-0 text-amber-600" />
          <div className="min-w-0 flex-1">
            <span className="font-bold text-amber-900 text-sm">Setup incomplete — </span>
            <span className="text-xs text-amber-700">{setup.data.message}</span>
          </div>
          <Link
            to="/hr/leave/setup"
            className="shrink-0 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-amber-700"
          >
            Open Setup →
          </Link>
        </div>
      )}

      {/* ── Reminder banners (slim) ── */}
      {reminderHint.shouldShow && reminderPlans.length > 0 && (
        <div className="space-y-1.5">
          {reminderPlans.map((p) => {
            const days = daysUntil(p.startDate);
            return (
              <div
                key={p.id}
                className="flex items-center gap-3 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2"
              >
                <Bell className="h-4 w-4 shrink-0 text-blue-500" />
                <span className="text-sm font-bold text-blue-900">{p.leaveTypeName}</span>
                <span className="flex-1 text-xs text-blue-700">
                  {days === 0
                    ? "starts today!"
                    : days === 1
                      ? "starts tomorrow"
                      : `in ${days} days`}
                  {" · "}
                  {fmtDate(p.startDate)} → {fmtDate(p.endDate)} · {p.plannedDays}d
                </span>
                <Link
                  to="/hr/leave/plans"
                  className="shrink-0 text-xs font-bold text-blue-600 hover:underline"
                >
                  Plans →
                </Link>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Stat chips (horizontal, compact) ── */}
      {canViewAll && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatChip
            label="On Leave Today"
            value={onLeaveList.length}
            loading={onLeave.loading}
            tone="emerald"
            to="/hr/leave/on-leave"
          />
          <StatChip
            label="Pending Approval"
            value={onLeave.data?.pendingApproval.length ?? null}
            loading={onLeave.loading}
            tone="amber"
            to={canApprove ? "/hr/leave/approvals" : undefined}
          />
          <StatChip
            label="Upcoming Leave"
            value={onLeave.data?.upcomingLeave.length ?? null}
            loading={onLeave.loading}
            tone="blue"
            to="/hr/leave/on-leave"
          />
          <StatChip
            label="On Official Duty"
            value={onDutyList.length}
            loading={onLeave.loading}
            tone="purple"
            to="/hr/leave/official-duty"
          />
        </div>
      )}

      {/* ── Main 12-col grid ── */}
      <div className="grid gap-4 lg:grid-cols-12">

        {/* ── Left: Balance + Plans + Config bar ── */}
        <div className="space-y-4 lg:col-span-7">

          {/* Balance */}
          {canApply && dashboardSummary.data && dashboardSummary.data.breakdown.length > 0 && (
            <div className="rounded-2xl border bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <h2 className="font-black text-slate-900">My Leave Balance</h2>
                </div>
                <Link
                  to="/hr/leave/my-leave"
                  className="text-xs font-bold text-emerald-700 hover:underline"
                >
                  View all →
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {dashboardSummary.data.breakdown.map((t) => {
                  const rem  = Number(t.remainingDays ?? 0);
                  const ent  = Number(t.entitledDays ?? 0);
                  const used = Number(t.usedDays ?? 0);
                  const pend = Number(t.pendingDays ?? 0);
                  const pct  = ent > 0 ? Math.min(100, Math.round((rem / ent) * 100)) : 100;
                  const tone = pct <= 0 ? "red" : pct < 40 ? "amber" : "emerald";
                  const bar  = { red: "bg-red-400", amber: "bg-amber-400", emerald: "bg-emerald-500" }[tone];
                  const num  = { red: "text-red-600", amber: "text-amber-700", emerald: "text-emerald-700" }[tone];
                  const gender = t.applicableGender;
                  return (
                    <div key={t.leaveTypeId} className="rounded-xl border bg-slate-50 p-3">
                      <div className="flex items-start justify-between gap-1">
                        <span className="text-xs font-bold leading-tight text-slate-600">
                          {t.leaveTypeName}
                        </span>
                        {gender && gender !== "ALL" && (
                          <span
                            className={`shrink-0 rounded-full px-1.5 py-0.5 text-xs font-bold ${
                              gender === "FEMALE"
                                ? "bg-pink-100 text-pink-700"
                                : "bg-blue-100 text-blue-700"
                            }`}
                          >
                            {gender === "FEMALE" ? "♀" : "♂"}
                          </span>
                        )}
                      </div>
                      <div className={`mt-1.5 text-2xl font-black ${num}`}>
                        {rem}
                        <span className="text-xs font-semibold text-slate-400">/{ent}d</span>
                      </div>
                      <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-slate-200">
                        <div className={`h-full rounded-full ${bar}`} style={{ width: `${pct}%` }} />
                      </div>
                      <div className="mt-1 flex gap-2 text-xs text-slate-400">
                        <span>{used} used</span>
                        {pend > 0 && <span className="text-amber-600">{pend} pending</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Plans */}
          {canApply && (
            <div className="rounded-2xl border bg-white p-4 shadow-sm">
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <h2 className="font-black text-slate-900">My Upcoming Plans</h2>
                </div>
                <Link
                  to="/hr/leave/plans"
                  className="text-xs font-bold text-emerald-700 hover:underline"
                >
                  Manage →
                </Link>
              </div>

              {myPlans.loading && <LeaveLoading label="Loading plans…" />}

              {!myPlans.loading && upcomingPlans.length === 0 && (
                <div className="rounded-xl border border-dashed py-6 text-center">
                  <CalendarCheck2 className="mx-auto h-7 w-7 text-slate-300" />
                  <p className="mt-1.5 text-xs text-slate-400">No upcoming plans scheduled.</p>
                  <Link
                    to="/hr/leave/plans"
                    className="mt-1 inline-block text-xs font-bold text-emerald-600 hover:underline"
                  >
                    + Add a plan
                  </Link>
                </div>
              )}

              {upcomingPlans.length > 0 && (
                <>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {upcomingPlans.slice(0, 4).map((p) => {
                      const days   = daysUntil(p.startDate);
                      const isNear = p.reminderEnabled && days <= (p.reminderDaysBefore ?? 14);
                      return (
                        <div
                          key={p.id}
                          className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 ${
                            isNear ? "border-blue-200 bg-blue-50" : "bg-slate-50"
                          }`}
                        >
                          <div
                            className={`flex h-9 w-9 shrink-0 flex-col items-center justify-center rounded-xl text-center ${
                              isNear ? "bg-blue-100" : "bg-emerald-100"
                            }`}
                          >
                            <span
                              className={`text-[10px] font-black uppercase leading-none ${
                                isNear ? "text-blue-600" : "text-emerald-600"
                              }`}
                            >
                              {new Date(`${p.startDate}T00:00:00`).toLocaleDateString(undefined, {
                                month: "short",
                              })}
                            </span>
                            <span
                              className={`text-base font-black leading-none ${
                                isNear ? "text-blue-700" : "text-emerald-700"
                              }`}
                            >
                              {new Date(`${p.startDate}T00:00:00`).getDate()}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-1.5">
                              <span className="truncate text-sm font-bold text-slate-900">
                                {p.leaveTypeName}
                              </span>
                              {isNear && <Bell className="h-3 w-3 shrink-0 text-blue-500" />}
                            </div>
                            <div className="truncate text-xs text-slate-500">
                              {fmtDate(p.startDate)} · {p.plannedDays}d
                              {days === 0
                                ? " · today!"
                                : days === 1
                                  ? " · tomorrow"
                                  : ` · in ${days}d`}
                            </div>
                          </div>
                          <span
                            className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-bold ${
                              p.status === "SUBMITTED"
                                ? "bg-emerald-100 text-emerald-700"
                                : "bg-slate-100 text-slate-500"
                            }`}
                          >
                            {p.status === "SUBMITTED" ? "Applied" : "Planned"}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  {upcomingPlans.length > 4 && (
                    <Link
                      to="/hr/leave/plans"
                      className="mt-2 block text-center text-xs font-bold text-emerald-600 hover:underline"
                    >
                      +{upcomingPlans.length - 4} more plans →
                    </Link>
                  )}
                </>
              )}
            </div>
          )}

          {/* People panel — tabbed On Leave / Official Duty */}
          <div className="overflow-hidden rounded-2xl border bg-white shadow-sm">
            {/* Tab bar */}
            <div className="flex border-b">
              <button
                type="button"
                onClick={() => setPeopleTab("leave")}
                className={`flex flex-1 items-center justify-center gap-1.5 py-2.5 text-xs font-bold transition-colors ${
                  peopleTab === "leave"
                    ? "border-b-2 border-emerald-600 text-emerald-700"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                On Leave
                <span
                  className={`rounded-full px-1.5 py-0.5 text-[10px] font-black ${
                    peopleTab === "leave"
                      ? "bg-emerald-100 text-emerald-700"
                      : "bg-slate-100 text-slate-500"
                  }`}
                >
                  {onLeaveList.length}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setPeopleTab("duty")}
                className={`flex flex-1 items-center justify-center gap-1.5 py-2.5 text-xs font-bold transition-colors ${
                  peopleTab === "duty"
                    ? "border-b-2 border-purple-600 text-purple-700"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                Official Duty
                <span
                  className={`rounded-full px-1.5 py-0.5 text-[10px] font-black ${
                    peopleTab === "duty"
                      ? "bg-purple-100 text-purple-700"
                      : "bg-slate-100 text-slate-500"
                  }`}
                >
                  {onDutyList.length}
                </span>
              </button>
            </div>

            {/* Loading / error */}
            {onLeave.loading && <LeaveLoading label="Loading…" />}
            {onLeave.error && <LeaveError message={onLeave.error} onRetry={onLeave.reload} />}

            {/* Content */}
            {!onLeave.loading && !onLeave.error && (
              <div className="p-3">
                {peopleTab === "leave" ? (
                  onLeaveList.length === 0 ? (
                    <p className="py-4 text-center text-xs text-slate-400">
                      No one on leave today.
                    </p>
                  ) : (
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                      {onLeaveList.slice(0, 6).map((e) => (
                        <Link
                          key={e.applicationId}
                          to={`/hr/leave/requests/${e.applicationId}`}
                          className="flex items-center gap-2 rounded-xl border bg-slate-50 px-3 py-2 hover:bg-emerald-50 hover:border-emerald-200 transition-colors"
                        >
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-xs font-black text-emerald-700">
                            {e.employeeName.charAt(0).toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <div className="truncate text-xs font-bold text-slate-800">
                              {e.employeeName}
                            </div>
                            <div className="truncate text-[10px] text-slate-400">
                              until {fmtDate(e.endDate)}
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )
                ) : onDutyList.length === 0 ? (
                  <p className="py-4 text-center text-xs text-slate-400">
                    No one on official duty.
                  </p>
                ) : (
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {onDutyList.slice(0, 6).map((e) => (
                      <div
                        key={e.id}
                        className="flex items-center gap-2 rounded-xl border bg-slate-50 px-3 py-2"
                      >
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-purple-100 text-xs font-black text-purple-700">
                          {(e.employeeName ?? "?").charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="truncate text-xs font-bold text-slate-800">
                            {e.employeeName ?? "—"}
                          </div>
                          <div className="truncate text-[10px] text-slate-400">
                            {e.location ? `${e.location} · ` : ""}until {fmtDate(e.endDate)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Overflow + pending nudge footer */}
            {((peopleTab === "leave" && onLeaveList.length > 6) ||
              (peopleTab === "duty" && onDutyList.length > 6)) && (
              <div className="border-t px-4 py-2 text-center">
                <Link
                  to={peopleTab === "leave" ? "/hr/leave/on-leave" : "/hr/leave/official-duty"}
                  className="text-xs font-bold text-emerald-600 hover:underline"
                >
                  View all →
                </Link>
              </div>
            )}
            {peopleTab === "leave" &&
              !onLeave.loading &&
              (onLeave.data?.pendingApproval.length ?? 0) > 0 && (
                <div className="flex items-center justify-between border-t bg-amber-50 px-4 py-2">
                  <span className="text-xs font-bold text-amber-700">
                    {onLeave.data!.pendingApproval.length} awaiting approval
                  </span>
                  {canApprove && (
                    <Link
                      to="/hr/leave/approvals"
                      className="text-xs font-bold text-amber-700 hover:underline"
                    >
                      Review →
                    </Link>
                  )}
                </div>
              )}
          </div>
        </div>

        {/* ── Right: Quick Actions + Tabbed people panel ── */}
        <div className="space-y-4 lg:col-span-5">

          {/* Quick Actions — 2-col icon tile grid */}
          <div className="rounded-2xl border bg-white p-4 shadow-sm">
            <h2 className="mb-3 text-xs font-black uppercase tracking-widest text-slate-400">
              Quick Actions
            </h2>
            <div className="grid grid-cols-2 gap-2">
              {canApply && (
                <ActionTile
                  to="/hr/leave/requests/new"
                  icon={FilePlus2}
                  label="Apply for Leave"
                  accent="emerald"
                />
              )}
              <ActionTile to="/hr/leave/my-leave" icon={CalendarDays} label="My Leave & Balances" />
              {canApply && (
                <ActionTile to="/hr/leave/plans" icon={CalendarCheck2} label="Leave Plans" />
              )}
              {canApprove && (
                <ActionTile
                  to="/hr/leave/approvals"
                  icon={ClipboardCheck}
                  label="Approvals"
                  badge={onLeave.data?.pendingApproval.length}
                />
              )}
              <ActionTile to="/hr/leave/on-leave" icon={Users} label="Who's on Leave" badge={onLeaveList.length || undefined} />
              <ActionTile
                to="/hr/leave/official-duty"
                icon={Briefcase}
                label="Official Duty"
                badge={onDutyList.length || undefined}
              />
              {canViewReports && (
                <ActionTile to="/hr/leave/reports" icon={BarChart2} label="Reports" />
              )}
              {canManage && (
                <ActionTile to="/hr/leave/setup" icon={Settings} label="Setup & Config" />
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

// ─── sub-components ───────────────────────────────────────────────────────────

function StatChip({
  label,
  value,
  loading,
  tone,
  to,
}: {
  label: string;
  value: number | null;
  loading: boolean;
  tone: "emerald" | "amber" | "blue" | "purple";
  to?: string;
}) {
  const bg  = {
    emerald: "bg-emerald-50 border-emerald-100",
    amber:   "bg-amber-50 border-amber-100",
    blue:    "bg-blue-50 border-blue-100",
    purple:  "bg-purple-50 border-purple-100",
  }[tone];
  const num = {
    emerald: "text-emerald-700",
    amber:   "text-amber-700",
    blue:    "text-blue-700",
    purple:  "text-purple-700",
  }[tone];
  const lbl = {
    emerald: "text-emerald-600",
    amber:   "text-amber-600",
    blue:    "text-blue-600",
    purple:  "text-purple-600",
  }[tone];
  const content = (
    <>
      <div className={`text-2xl font-black ${num}`}>
        {loading ? <span className="text-base opacity-40">…</span> : (value ?? 0)}
      </div>
      <div className={`text-xs font-bold leading-tight ${lbl} opacity-80`}>{label}</div>
    </>
  );

  if (!to) {
    return (
      <div className={`flex items-center gap-3 rounded-2xl border ${bg} px-4 py-3 shadow-sm`}>
        {content}
      </div>
    );
  }

  return (
    <Link
      to={to}
      className={`flex items-center gap-3 rounded-2xl border ${bg} px-4 py-3 shadow-sm transition hover:shadow-md`}
    >
      {content}
    </Link>
  );
}

function ActionTile({
  to,
  icon: Icon,
  label,
  accent,
  badge,
}: {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  accent?: "emerald";
  badge?: number;
}) {
  return (
    <Link
      to={to}
      className={`relative flex flex-col items-center gap-2 rounded-xl p-3 text-center text-xs font-bold transition-colors ${
        accent === "emerald"
          ? "bg-emerald-700 text-white hover:bg-emerald-800"
          : "border bg-slate-50 text-slate-700 hover:bg-slate-100"
      }`}
    >
      <Icon
        className={`h-5 w-5 ${accent === "emerald" ? "text-emerald-100" : "text-emerald-600"}`}
      />
      <span className="leading-tight">{label}</span>
      {badge !== undefined && badge > 0 && (
        <span className="absolute right-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-[9px] font-black text-white">
          {badge > 9 ? "9+" : badge}
        </span>
      )}
    </Link>
  );
}
